import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCheck, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import useReadStatus from "@/hooks/useReadStatus";
import useNavigationNotifications from "@/hooks/useNavigationNotifications";
import { motion, AnimatePresence } from "framer-motion";

interface MarkAllReadButtonProps {
  className?: string;
  variant?: "default" | "ghost" | "outline";
  size?: "default" | "sm" | "lg" | "icon";
  showLabel?: boolean;
}

export const MarkAllReadButton = ({
  className,
  variant = "ghost",
  size = "sm",
  showLabel = true,
}: MarkAllReadButtonProps) => {
  const [isMarking, setIsMarking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { markAllAsRead } = useReadStatus();
  const { refetch, notifications } = useNavigationNotifications();
  const { toast } = useToast();

  const totalNotifications = 
    notifications.dashboard + 
    notifications.vault + 
    notifications.funZone + 
    notifications.viralStories + 
    notifications.hallOfFame;

  const handleMarkAllRead = async () => {
    if (totalNotifications === 0) return;
    
    setIsMarking(true);
    try {
      const success = await markAllAsRead([
        "drop_invites",
        "viral_stories",
        "hall_of_fame",
        "fun_zone",
      ]);

      if (success) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        await refetch();
        toast({
          title: "All caught up! ✨",
          description: "Notifications marked as read",
        });
      }
    } catch (error) {
      toast({
        title: "Couldn't mark as read",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsMarking(false);
    }
  };

  if (totalNotifications === 0) return null;

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleMarkAllRead}
      disabled={isMarking}
      className={className}
    >
      <AnimatePresence mode="wait">
        {isMarking ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <Loader2 className="w-4 h-4 animate-spin" />
          </motion.div>
        ) : showSuccess ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="text-green-500"
          >
            <CheckCheck className="w-4 h-4" />
          </motion.div>
        ) : (
          <motion.div
            key="default"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center gap-1.5"
          >
            <CheckCheck className="w-4 h-4" />
            {showLabel && <span>Mark all read</span>}
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

export default MarkAllReadButton;
